import React, { useEffect, useState } from "react";
import EmployeeHeader from "../components/EmployeeHeader";
import EmployeeFooter from "../components/EmployeeFooter";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMapEvents,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import RecenterButton from "../components/RecenterButton";
import L from "leaflet";

// Fix Leaflet icon paths (important for markers to show)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet/dist/images/marker-shadow.png",
});

// Dummy vehicles data (replace with backend later)
const dummyVehicles = [
  { id: 1, type: "Forklift", lat: 6.9570, lng: 79.8705 },
  { id: 2, type: "Cherry Picker", lat: 6.9582, lng: 79.8721 },
  { id: 3, type: "Forklift", lat: 6.9558, lng: 79.8700 },
];

// Component that listens to map clicks and sets start/end points
const LocationSelector = ({ start, end, setStart, setEnd }) => {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      // first click -> start, second click -> end, further clicks ignored until cleared
      if (!start) {
        setStart({ lat, lng });
      } else if (!end) {
        setEnd({ lat, lng });
      }
    },
  });
  return null;
};

const EmployeeNearbyVehicles = () => {
  const [userLocation, setUserLocation] = useState(null);
  const [startPoint, setStartPoint] = useState(null);
  const [endPoint, setEndPoint] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [geoError, setGeoError] = useState(null);

  useEffect(() => {
    if (!navigator.geolocation) {
      setGeoError("Geolocation is not supported by your browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) =>
        setUserLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        }),
      (err) => {
        setGeoError("Unable to retrieve your location. Allow location access.");
        console.error("Geolocation error:", err);
      },
      { enableHighAccuracy: true }
    );
  }, []);

  const clearSelection = () => {
    setStartPoint(null);
    setEndPoint(null);
    setSelectedVehicle(null);
  };

  // simple haversine distance (km)
  const haversine = (a, b) => {
    if (!a || !b) return null;
    const toRad = (v) => (v * Math.PI) / 180;
    const R = 6371; // km
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);

    const h =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
    return R * c;
  };

  const routeDistance =
    startPoint && endPoint ? haversine(startPoint, endPoint).toFixed(2) : null;

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <EmployeeHeader />

      <main className="flex-1 max-w-7xl mx-auto w-full p-6">
        <h2 className="text-xl font-bold text-blue-900 mb-4">
          🚜 Nearby Vehicles + Route Setup
        </h2>

        {geoError && (
          <div className="mb-4 p-3 bg-amber-50 border-l-4 border-red-500 text-red-800 rounded">
            {geoError}
          </div>
        )}

        {userLocation ? (
          <>
            <div className="h-[70vh] w-full rounded-lg overflow-hidden shadow-lg border border-gray-300">
              <MapContainer
                center={[userLocation.lat, userLocation.lng]}
                zoom={17}
                scrollWheelZoom={true}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <RecenterButton position={[userLocation.lat, userLocation.lng]} zoom={17} />
                {/* listen for clicks */}
                <LocationSelector
                  start={startPoint}
                  end={endPoint}
                  setStart={setStartPoint}
                  setEnd={setEndPoint}
                />

                {/* user marker */}
                <Marker position={[userLocation.lat, userLocation.lng]}>
                  <Popup>You are here</Popup>
                </Marker>

                {/* vehicle markers */}
                {dummyVehicles.map((v) => (
                  <Marker
                    key={v.id}
                    position={[v.lat, v.lng]}
                    eventHandlers={{
                      click: () => setSelectedVehicle(v),
                    }}
                  >
                    <Popup>
                      <b>{v.type}</b>
                      <br />
                      Vehicle ID: {v.id}
                    </Popup>
                  </Marker>
                ))}

                {/* start & end markers */}
                {startPoint && (
                  <Marker position={[startPoint.lat, startPoint.lng]}>
                    <Popup>Start Point</Popup>
                  </Marker>
                )}
                {endPoint && (
                  <Marker position={[endPoint.lat, endPoint.lng]}>
                    <Popup>End Point</Popup>
                  </Marker>
                )}

                {/* route polyline */}
                {startPoint && endPoint && (
                  <Polyline
                    positions={[
                      [startPoint.lat, startPoint.lng],
                      [endPoint.lat, endPoint.lng],
                    ]}
                  />
                )}
              </MapContainer>
            </div>

            {/* Info panel */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded shadow">
                <h3 className="font-semibold mb-2">Selected Vehicle</h3>
                {selectedVehicle ? (
                  <div>
                    <div className="text-sm">ID: {selectedVehicle.id}</div>
                    <div className="text-sm">Type: {selectedVehicle.type}</div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-600">Click a vehicle marker to select</div>
                )}
              </div>

              <div className="bg-white p-4 rounded shadow">
                <h3 className="font-semibold mb-2">Route</h3>
                <div className="text-sm">
                  Start:{" "}
                  {startPoint ? `${startPoint.lat.toFixed(6)}, ${startPoint.lng.toFixed(6)}` : "—"}
                </div>
                <div className="text-sm">
                  End: {endPoint ? `${endPoint.lat.toFixed(6)}, ${endPoint.lng.toFixed(6)}` : "—"}
                </div>
                {routeDistance && <div className="text-sm mt-2">Distance: {routeDistance} km</div>}
              </div>

              <div className="bg-white p-4 rounded shadow flex flex-col gap-2">
                <button
                  onClick={() => {
                    if (!startPoint || !endPoint || !selectedVehicle) {
                      alert("Select start, end and vehicle first.");
                      return;
                    }
                    // Replace with booking flow (open modal or navigate to booking page)
                    alert(
                      `Ready to book Vehicle ${selectedVehicle.id}\nStart: ${startPoint.lat.toFixed(
                        6
                      )}, ${startPoint.lng.toFixed(6)}\nEnd: ${endPoint.lat.toFixed(
                        6
                      )}, ${endPoint.lng.toFixed(6)}`
                    );
                  }}
                  className="bg-green-700 text-white px-4 py-2 rounded hover:bg-green-600"
                >
                  Confirm & Book
                </button>

                <button
                  onClick={clearSelection}
                  className="bg-gray-200 text-gray-800 px-3 py-2 rounded hover:bg-gray-300"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          </>
        ) : (
          <p className="text-gray-700">Fetching location...</p>
        )}
      </main>

      <EmployeeFooter />
    </div>
  );
};

export default EmployeeNearbyVehicles;
